import { createBrowserRouter, Navigate } from 'react-router';
import { AppBoot, PublicOnly, RequireAuth, RouteError } from './components/Guards.jsx';
import { Layout } from './components/Layout.jsx';
import { ForgotPasswordPage, LoginPage, MfaPage, PasswordLinkPage } from './features/auth/AuthPages.jsx';
import { DashboardPage } from './features/dashboard/DashboardPage.jsx';

/** Code-split route: the module is fetched the first time someone opens that page. */
const page = (load, name) => async () => ({ Component: (await load())[name] });
const clientTab = (name) => page(() => import('./features/clients/tabs/index.js'), name);

export const router = createBrowserRouter([
  {
    element: <AppBoot />,
    errorElement: <RouteError />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/login/mfa', element: <MfaPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
        ],
      },
      { path: '/reset-password', element: <PasswordLinkPage mode="reset" /> },
      { path: '/set-password', element: <PasswordLinkPage mode="set" /> },
      {
        element: (
          <RequireAuth>
            <Layout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'clients', lazy: page(() => import('./features/clients/ClientsPage.jsx'), 'ClientsPage') },
          {
            path: 'clients/new',
            lazy: page(() => import('./features/clients/ClientCreatePage.jsx'), 'ClientCreatePage'),
          },
          {
            path: 'clients/:id',
            lazy: page(() => import('./features/clients/ClientDetailPage.jsx'), 'ClientDetailPage'),
            children: [
              { index: true, lazy: clientTab('OverviewTab') },
              { path: 'license', lazy: clientTab('LicenseTab') },
              { path: 'outlets', lazy: clientTab('OutletsTab') },
              { path: 'users', lazy: clientTab('UsersTab') },
              { path: 'audit', lazy: clientTab('AuditTab') },
              { path: 'notes', lazy: clientTab('NotesTab') },
              { path: 'emails', lazy: clientTab('EmailsTab') },
              { path: 'integrations', lazy: clientTab('IntegrationsTab') },
            ],
          },
          { path: 'emails', lazy: page(() => import('./features/emails/EmailLogsPage.jsx'), 'EmailLogsPage') },
          {
            path: 'announcements',
            lazy: page(() => import('./features/announcements/AnnouncementsPage.jsx'), 'AnnouncementsPage'),
          },
          { path: 'plans', lazy: page(() => import('./features/plans/PlansPage.jsx'), 'PlansPage') },
          { path: 'admins', lazy: page(() => import('./features/admins/AdminUsersPage.jsx'), 'AdminUsersPage') },
          { path: 'settings', lazy: page(() => import('./features/settings/SettingsPage.jsx'), 'SettingsPage') },
          { path: 'account', lazy: page(() => import('./features/account/AccountPage.jsx'), 'AccountPage') },
          // Swagger UI is large: only loaded when someone opens the API docs.
          {
            path: 'api-docs',
            lazy: page(() => import('./features/apidocs/ApiDocsPage.jsx'), 'ApiDocsPage'),
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
